import { FC, useState } from 'react';

import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useSettingsStore } from 'app/store';

import { EditIcon, KeyboardArrow, UnfoldMoreIcon } from 'shared/assets/icons';
import { ROLES } from 'shared/constants';
import { useUser } from 'shared/hooks';
import { useCustomNavigate } from 'shared/hooks/useCustomNavigate';
import { TCashdesk } from 'shared/services/types/cashdesk.types';
import { RoleAccessWrapper } from 'shared/ui/role-access-wrapper';
import { StyledSwitch } from 'shared/ui/switch-styled';
import { CustomTooltip } from 'shared/ui/tooltip';

interface IProps {
  cashdeskItemData: TCashdesk.CashdeskData;
  onOpenModal: (
    modalType: 'info' | '',
    data: TCashdesk.CashdeskData | TCashdesk.CreateCashdesk
  ) => void;
  totalAgents: number;
  totalMethods: number;
}

export const CashDeskItem: FC<IProps> = ({
  cashdeskItemData,
  onOpenModal,
  totalAgents,
  totalMethods
}) => {
  const [checked, setChecked] = useState(true);
  const { customNavigate } = useCustomNavigate();
  const { user } = useUser();
  const { darkTheme } = useSettingsStore();
  return (
    <Grid size={{ sm: 12, md: 6, lg: 4, xl: 3 }} sx={{ width: 1 }}>
      <Stack
        sx={{
          p: 6,
          borderRadius: 4,
          backgroundColor: 'var(--mui-palette-background-paper)',
          border: '1px solid',
          borderColor: darkTheme ? 'var(--mui-palette-divider)' : 'transparent'
        }}>
        <Stack
          sx={{
            width: 1,
            gap: 14,
            pb: 10,
            flexDirection: 'row'
          }}>
          <Typography
            variant='body1'
            color='primary'
            sx={{ fontSize: 18, lineHeight: '25px', fontWeight: '600', flex: 1 }}>
            {cashdeskItemData.name}
          </Typography>

          <Box>
            <StyledSwitch
              value={checked}
              setValue={() => {
                setChecked(prev => !prev);
              }}
            />
          </Box>

          <RoleAccessWrapper accessCondition={ROLES.CashDesk.canEdit} currentRole={user?.role}>
            <CustomTooltip title='Edit cash desk'>
              <Button
                variant='outlined'
                onClick={() => {
                  onOpenModal('', cashdeskItemData);
                }}
                sx={{
                  p: '4px 10px',
                  minWidth: '36px'
                }}>
                <EditIcon />
              </Button>
            </CustomTooltip>
          </RoleAccessWrapper>
          <CustomTooltip title='Open cash desk'>
            <Button
              variant='outlined'
              onClick={() => {
                customNavigate(`/cashdesk/${cashdeskItemData.id}`);
              }}
              sx={{
                p: '4px 10px',
                minWidth: '36px'
              }}>
              <UnfoldMoreIcon />
            </Button>
          </CustomTooltip>
        </Stack>
        <Stack
          sx={{
            width: 1,
            gap: 6,
            flexDirection: 'column',
            '.MuiTypography-root': {
              letterSpacing: '0.25px',
              color: 'var(--mui-palette-text-modalDark)'
            }
          }}>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between'
            }}>
            <Typography
              variant='body1'
              sx={{ fontSize: 16, lineHeight: '20px', fontWeight: '600' }}>
              Total
            </Typography>

            <Typography
              variant='body1'
              sx={{ fontSize: 16, lineHeight: '20px', fontWeight: '600' }}>
              Count
            </Typography>
          </Stack>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between'
            }}>
            <Typography
              variant='body1'
              sx={{
                fontSize: 15,
                lineHeight: '20px',
                fontWeight: '500',
                '&.MuiTypography-root': {
                  color: 'var(--mui-palette-text-cashdeskRowInfo)'
                }
              }}>
              Agent:
            </Typography>

            <Typography
              variant='body1'
              sx={{ fontSize: 15, lineHeight: '20px', fontWeight: '600' }}>
              {totalAgents}
            </Typography>
          </Stack>
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between'
            }}>
            <Typography
              variant='body1'
              sx={{
                fontSize: 15,
                lineHeight: '20px',
                fontWeight: '500',
                '&.MuiTypography-root': {
                  color: 'var(--mui-palette-text-cashdeskRowInfo)'
                }
              }}>
              Method:
            </Typography>

            <Typography
              variant='body1'
              // color='textModalDark'
              sx={{ fontSize: 15, lineHeight: '20px', fontWeight: '600' }}>
              {totalMethods}
            </Typography>
          </Stack>
          <Stack
            sx={{
              width: 1
            }}>
            <Button
              endIcon={<KeyboardArrow />}
              variant='outlined'
              onClick={() => {
                onOpenModal('info', {
                  ...cashdeskItemData,
                  totalMethods: totalMethods,
                  totalAgents: totalAgents
                });
              }}
              sx={{
                p: 0,
                textDecoration: 'underline',
                backgroundColor: 'transparent',
                justifyContent: 'space-between',
                lineHeight: '20px',
                fontSize: 15,
                border: 'none'
              }}>
              More info
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Grid>
  );
};
